import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    BehaviorSubject,
    catchError,
    defer,
    finalize,
    from,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { LoggerService } from '@pf/shared-services';
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper';
import { asArraySafe, compareSafe } from '@pf/shared-utility';
import { Device } from 'zebra-browser-print-wrapper/lib/types';

export interface ZebraPrintZplOptions {
    printerAddress?: string;
    printerUid?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ZebraBrowserPrintService {
    private readonly http = inject(HttpClient);
    private readonly logger = inject(LoggerService);

    private readonly availablePrintersSubject = new BehaviorSubject<
        Device[] | null
    >(null);
    readonly availablePrinters$ = this.availablePrintersSubject
        .asObservable()
        .pipe(
            map(printers => printers ?? []),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    private availablePrintersRequest$?: Observable<Device[]>;

    getAvailablePrinters(): Observable<Device[]> {
        const cached = this.availablePrintersSubject.value;
        if (cached !== null) {
            return of(cached);
        }

        if (this.availablePrintersRequest$) {
            return this.availablePrintersRequest$;
        }

        this.availablePrintersRequest$ = this.refreshAvailablePrinters();
        return this.availablePrintersRequest$;
    }

    refreshAvailablePrinters(): Observable<Device[]> {
        const browserPrint = new ZebraBrowserPrintWrapper();

        return from(browserPrint.getAvailablePrinters()).pipe(
            map((printers: Device[] | null) => asArraySafe(printers)),
            catchError(error => {
                this.logger.error(
                    'Error getting BrowserPrint printers:',
                    error
                );
                return of([]);
            }),
            tap(printers => this.availablePrintersSubject.next(printers)),
            finalize(() => {
                this.availablePrintersRequest$ = undefined;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    printZpl(zpl: string, options: ZebraPrintZplOptions): Observable<boolean> {
        if (options.printerAddress) {
            return this.printViaDirectPrint(zpl, options.printerAddress);
        }

        return this.printViaBrowserPrint(zpl, options.printerUid);
    }

    private printViaDirectPrint(
        zpl: string,
        printerAddress: string
    ): Observable<boolean> {
        return defer(() => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000); // 2s timeout

            return from(
                fetch(`http://${printerAddress}:9100/print`, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: { 'Content-Type': 'text/plain' },
                    body: zpl,
                    signal: controller.signal,
                    keepalive: false,
                })
            ).pipe(
                map(() => {
                    clearTimeout(timeoutId);
                    return true;
                }),
                catchError(err => {
                    clearTimeout(timeoutId);
                    if (err.name !== 'AbortError') {
                        this.logger.error('Print Job Error:', err);
                    }
                    return of(false);
                })
            );
        });
    }

    printViaBrowserPrint(
        zpl: string,
        printerUid?: string
    ): Observable<boolean> {
        const browserPrint = new ZebraBrowserPrintWrapper();

        const printer$ = printerUid
            ? this.getAvailablePrinters().pipe(
                  map((printers: Device[]) => {
                      const match = printers.find(printer => {
                          return compareSafe(printerUid, printer.uid);
                      });
                      if (!match) {
                          throw new Error(`Printer not found: ${printerUid}.`);
                      }
                      return match;
                  })
              )
            : from(browserPrint.getDefaultPrinter());

        return printer$.pipe(
            switchMap(printer => {
                browserPrint.setPrinter(printer);
                return from(browserPrint.checkPrinterStatus());
            }),
            switchMap(status => {
                if (!status?.isReadyToPrint) {
                    return throwError(
                        () => new Error(status?.errors || 'Printer not ready.')
                    );
                }
                return from(browserPrint.write(zpl));
            }),
            map(() => true),
            catchError(error => {
                this.logger.error('Error printing via BrowserPrint:', error);
                return throwError(
                    () =>
                        new Error(
                            (error as Error)?.message ||
                                'BrowserPrint printing failed.'
                        )
                );
            })
        );
    }
}
